/**
 * EqFootnotes — client-side numbering for crawler-clean placeholders.
 *
 * Authoring (CMS):
 *   reference:  {{fn:nav}}
 *   definition: [nav] Some text.
 *
 * PHP Footnotes::sanitize() rewrites those into:
 *   <sup class="eq-fn-ref" data-fn="nav"></sup>
 *   <p class="eq-fn-def" data-fn="nav">Some text.</p>
 *
 * This module assigns numbers, builds links, and fills ol.footnotes-list.
 * Falls back to raw {{fn:key}} / [key] if PHP sanitizer did not run.
 */
(function (window, document) {
    'use strict';

    var REFERENCE_RE = /\{\{\s*fn\s*:\s*([A-Za-z0-9][A-Za-z0-9_-]*)\s*\}\}/g;
    var MARKER_RE = /^\s*\[([A-Za-z0-9][A-Za-z0-9_-]*)\]\s*/;
    var READY_CLASS = 'eq-footnotes-ready';
    var SCROLL_OFFSET = 300;
    var FAILSAFE_MS = 2000;
    var ATTR_NAMES = ['aria-label', 'alt', 'title', 'placeholder', 'data-original-title'];

    var initialized = false;
    var failsafeTimer = null;
    var navigationBound = false;

    function markReady() {
        document.documentElement.classList.add(READY_CLASS);
        if (failsafeTimer !== null) {
            window.clearTimeout(failsafeTimer);
            failsafeTimer = null;
        }
    }

    function scheduleFailsafe() {
        if (failsafeTimer !== null || document.documentElement.classList.contains(READY_CLASS)) {
            return;
        }
        failsafeTimer = window.setTimeout(markReady, FAILSAFE_MS);
    }

    function warn(message) {
        if (typeof console !== 'undefined' && console.warn) {
            console.warn('[EqFootnotes] ' + message);
        }
    }

    // REFERENCE_RE is global, so lastIndex must be reset around every reuse.
    function hasReferenceToken(text) {
        if (!text) {
            return false;
        }
        REFERENCE_RE.lastIndex = 0;
        var found = REFERENCE_RE.test(String(text));
        REFERENCE_RE.lastIndex = 0;
        return found;
    }

    function stripTokens(text) {
        if (!hasReferenceToken(text)) {
            return text;
        }
        REFERENCE_RE.lastIndex = 0;
        return String(text).replace(REFERENCE_RE, '');
    }

    function stripAttributeTokens(root) {
        var i;
        var j;
        var el;
        var name;
        var value;
        var nodes = root.querySelectorAll('[' + ATTR_NAMES.join('],[') + ']');

        for (i = 0; i < nodes.length; i++) {
            el = nodes[i];
            for (j = 0; j < ATTR_NAMES.length; j++) {
                name = ATTR_NAMES[j];
                if (!el.hasAttribute(name)) {
                    continue;
                }
                value = el.getAttribute(name);
                if (hasReferenceToken(value)) {
                    el.setAttribute(name, stripTokens(value));
                }
            }
        }

        var scripts = root.querySelectorAll('script[type="application/json"]');
        for (i = 0; i < scripts.length; i++) {
            if (hasReferenceToken(scripts[i].textContent)) {
                scripts[i].textContent = stripTokens(scripts[i].textContent);
            }
        }
    }

    function stripMarkerFromElement(el) {
        var first = el.firstChild;
        while (first && first.nodeType === 1 && !(first.textContent || '').trim()) {
            el.removeChild(first);
            first = el.firstChild;
        }
        if (!first) {
            return;
        }
        if (first.nodeType === 3) {
            first.textContent = first.textContent.replace(MARKER_RE, '');
            return;
        }
        if (first.nodeType === 1) {
            var textNode = first;
            while (textNode && textNode.nodeType === 1) {
                textNode = textNode.firstChild;
            }
            if (!textNode || textNode.nodeType !== 3) {
                return;
            }
            var markerText = (textNode.textContent || '').replace(/\u00a0/g, ' ');
            if (MARKER_RE.test(markerText.trim())) {
                textNode.textContent = markerText.replace(MARKER_RE, '');
                if (!(textNode.textContent || '').trim() && textNode.parentNode) {
                    textNode.parentNode.removeChild(textNode);
                }
                if (!(first.textContent || '').trim() && first.childNodes.length === 0) {
                    el.removeChild(first);
                }
            }
        }
    }

    function collectDefinitions(root) {
        var definitions = {};
        var i;
        var el;
        var key;
        var clone;
        var text;
        var match;

        // Prefer PHP-sanitized definition blocks.
        var defs = root.querySelectorAll('.eq-fn-def[data-fn], [data-fn].eq-fn-def');
        for (i = 0; i < defs.length; i++) {
            el = defs[i];
            key = el.getAttribute('data-fn');
            if (!key || definitions[key]) {
                continue;
            }
            definitions[key] = {
                html: el.innerHTML.trim(),
                source: el
            };
        }

        // Fallback: [key] markers still in the HTML.
        var blocks = root.querySelectorAll('p, li');
        for (i = 0; i < blocks.length; i++) {
            el = blocks[i];
            if (el.getAttribute('data-fn') && el.classList.contains('eq-fn-def')) {
                continue;
            }
            text = (el.textContent || '').replace(/\u00a0/g, ' ').trim();
            match = text.match(MARKER_RE);
            if (!match || definitions[match[1]]) {
                continue;
            }
            key = match[1];
            clone = el.cloneNode(true);
            stripMarkerFromElement(clone);
            definitions[key] = {
                html: clone.innerHTML.trim(),
                source: el
            };
        }

        return definitions;
    }

    function fillSuperscript(sup, number, refCount) {
        var link = document.createElement('a');
        var refId = 'footnote-ref-' + number + (refCount > 1 ? '-' + refCount : '');
        link.className = 'footnote-anchor';
        link.id = refId;
        link.href = '#footnote-' + number;
        link.setAttribute('aria-describedby', 'footnote-' + number + '-desc');
        link.textContent = String(number);
        while (sup.firstChild) {
            sup.removeChild(sup.firstChild);
        }
        if (!sup.classList.contains('eq-fn-ref')) {
            sup.classList.add('eq-fn-ref');
        }
        sup.appendChild(link);
        return link;
    }

    function createSuperscript(number, refCount) {
        var sup = document.createElement('sup');
        fillSuperscript(sup, number, refCount);
        return sup;
    }

    function registerKey(key, definitions, footnotes, order, hasList) {
        if (!hasList) {
            warn('footnote [' + key + '] needs a Footnotes row on this page');
            return null;
        }
        if (!definitions[key]) {
            warn('no [' + key + '] footnote text found on this page');
            return null;
        }
        if (!footnotes[key]) {
            footnotes[key] = {
                number: order.length + 1,
                html: definitions[key].html,
                refs: 0,
                source: definitions[key].source
            };
            order.push(key);
        }
        footnotes[key].refs += 1;
        return footnotes[key];
    }

    function linkPlaceholderRefs(root, definitions, footnotes, order) {
        var hasList = !!root.querySelector('ol.footnotes-list');
        var refs = root.querySelectorAll('sup.eq-fn-ref[data-fn]');
        var i;
        var el;
        var key;
        var footnote;

        for (i = 0; i < refs.length; i++) {
            el = refs[i];
            key = el.getAttribute('data-fn');
            if (!key) {
                continue;
            }
            footnote = registerKey(key, definitions, footnotes, order, hasList);
            if (!footnote) {
                el.parentNode && el.parentNode.removeChild(el);
                continue;
            }
            fillSuperscript(el, footnote.number, footnote.refs);
        }
    }

    function walkTokenTextNodes(root, callback) {
        var walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
            acceptNode: function (node) {
                var parent = node.parentNode;
                if (!parent) {
                    return NodeFilter.FILTER_REJECT;
                }
                var tag = parent.nodeName;
                if (tag === 'SCRIPT' || tag === 'STYLE' || tag === 'NOSCRIPT' || tag === 'TEXTAREA') {
                    return NodeFilter.FILTER_REJECT;
                }
                if (!hasReferenceToken(node.nodeValue)) {
                    return NodeFilter.FILTER_REJECT;
                }
                return NodeFilter.FILTER_ACCEPT;
            }
        });

        var node;
        var nodes = [];
        while ((node = walker.nextNode())) {
            nodes.push(node);
        }
        for (var i = 0; i < nodes.length; i++) {
            callback(nodes[i]);
        }
    }

    function linkTokenRefs(root, definitions, footnotes, order) {
        var hasList = !!root.querySelector('ol.footnotes-list');

        walkTokenTextNodes(root, function (textNode) {
            var text = textNode.nodeValue;
            var frag = document.createDocumentFragment();
            var lastIndex = 0;
            var match;
            var key;
            var footnote;

            REFERENCE_RE.lastIndex = 0;
            while ((match = REFERENCE_RE.exec(text)) !== null) {
                if (match.index > lastIndex) {
                    frag.appendChild(document.createTextNode(text.slice(lastIndex, match.index)));
                }

                key = match[1];
                footnote = registerKey(key, definitions, footnotes, order, hasList);
                if (footnote) {
                    frag.appendChild(createSuperscript(footnote.number, footnote.refs));
                }
                lastIndex = REFERENCE_RE.lastIndex;
            }

            if (lastIndex < text.length) {
                frag.appendChild(document.createTextNode(text.slice(lastIndex)));
            }

            textNode.parentNode.replaceChild(frag, textNode);
        });
    }

    function removeDefinitionSources(order, footnotes) {
        var removed = [];
        var i;
        var key;
        var el;
        var parent;

        for (i = 0; i < order.length; i++) {
            key = order[i];
            el = footnotes[key].source;
            if (!el || removed.indexOf(el) !== -1) {
                continue;
            }
            parent = el.parentNode;
            if (parent) {
                parent.removeChild(el);
            }
            removed.push(el);

            if (parent && parent.classList && parent.classList.contains('source') && !(parent.textContent || '').trim()) {
                if (parent.parentNode) {
                    parent.parentNode.removeChild(parent);
                }
            }
            if (parent && (parent.nodeName === 'OL' || parent.nodeName === 'UL') && !parent.children.length) {
                if (parent.parentNode) {
                    parent.parentNode.removeChild(parent);
                }
            }
        }
    }

    function stripUnreferencedMarkers(root, footnotes) {
        var blocks = root.querySelectorAll('p, li');
        var i;
        var el;
        var text;
        var match;

        for (i = 0; i < blocks.length; i++) {
            el = blocks[i];
            if (el.classList && el.classList.contains('eq-fn-def') && el.getAttribute('data-fn')) {
                // Unreferenced sanitized defs: leave in place without marker (already clean).
                continue;
            }
            text = (el.textContent || '').replace(/\u00a0/g, ' ').trim();
            match = text.match(MARKER_RE);
            if (!match || footnotes[match[1]]) {
                continue;
            }
            stripMarkerFromElement(el);
        }
    }

    function renderList(root, order, footnotes) {
        var list = root.querySelector('ol.footnotes-list');
        if (!list) {
            return;
        }

        list.innerHTML = '';
        if (!order.length) {
            return;
        }

        list.setAttribute('data-rendered', 'js');

        var i;
        var key;
        var footnote;
        var li;
        var p;
        var link;
        var wrap;

        for (i = 0; i < order.length; i++) {
            key = order[i];
            footnote = footnotes[key];
            li = document.createElement('li');
            li.id = 'footnote-' + footnote.number;
            li.setAttribute('tabindex', '-1');
            p = document.createElement('p');
            p.className = 'footnote-desc';
            link = document.createElement('a');
            link.className = 'footnote-return';
            link.href = '#footnote-ref-' + footnote.number;
            link.setAttribute('aria-label', 'Back to reference ' + footnote.number);
            link.textContent = String(footnote.number);
            p.appendChild(link);
            p.appendChild(document.createTextNode('. '));
            wrap = document.createElement('span');
            wrap.id = 'footnote-' + footnote.number + '-desc';
            wrap.innerHTML = footnote.html;
            p.appendChild(wrap);
            li.appendChild(p);
            list.appendChild(li);
        }
    }

    function activateFootnoteTarget(el) {
        if (!el) {
            return;
        }
        if (el.nodeName !== 'A' && el.nodeName !== 'BUTTON' && !el.hasAttribute('tabindex')) {
            el.setAttribute('tabindex', '-1');
        }
        window.scrollTo(0, el.getBoundingClientRect().top + window.pageYOffset - SCROLL_OFFSET);
        el.focus({ preventScroll: true });
    }

    function bindNavigation() {
        if (navigationBound) {
            return;
        }
        navigationBound = true;

        if (typeof jq === 'undefined') {
            document.addEventListener('click', function (e) {
                var target = e.target.closest ? e.target.closest('.footnote-anchor, .footnote-return') : null;
                if (!target) {
                    return;
                }
                var hash = target.getAttribute('href');
                if (!hash || hash.charAt(0) !== '#') {
                    return;
                }
                var el = document.getElementById(hash.slice(1));
                if (!el) {
                    return;
                }
                e.preventDefault();
                activateFootnoteTarget(el);
            });
            return;
        }

        jq(document).on('click', '.footnote-anchor, .footnote-return', function (e) {
            var hash = jq(this).attr('href');
            var el;
            if (!hash || hash.charAt(0) !== '#') {
                return;
            }
            el = document.getElementById(hash.slice(1));
            if (!el) {
                return;
            }
            e.preventDefault();
            activateFootnoteTarget(el);
        });
    }

    function init(root) {
        root = root || document;
        scheduleFailsafe();

        try {
            stripAttributeTokens(root);

            var definitions = collectDefinitions(root);
            var footnotes = {};
            var order = [];

            linkPlaceholderRefs(root, definitions, footnotes, order);
            linkTokenRefs(root, definitions, footnotes, order);

            if (order.length) {
                removeDefinitionSources(order, footnotes);
                renderList(root, order, footnotes);
            }

            stripUnreferencedMarkers(root, footnotes);
        } catch (err) {
            warn(err && err.message ? err.message : String(err));
        }

        markReady();
    }

    function boot() {
        if (initialized) {
            return;
        }
        initialized = true;
        scheduleFailsafe();
        bindNavigation();
        init(document);
    }

    window.EqFootnotes = {
        init: init,
        markReady: markReady
    };

    scheduleFailsafe();

    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', boot);
    } else {
        boot();
    }
}(window, document));
